import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  getOperaCandidates,
  getOperaCovers,
  getOperaGuestProfile,
  getOperaHotelGuests,
  getOperaMatchAttempts,
  linkOperaReservation,
  refreshOperaLink,
  rejectOperaMatch,
} from './apiHelpers';

export const operaLinkQueryKey = (
  restaurantId: string,
  reservationId: string,
) => ['restaurants', restaurantId, 'opera', reservationId] as const;

const useOperaCandidatesEndpoint = (reservationId: string, enabled = true) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: operaLinkQueryKey(restaurantId, reservationId),
    queryFn: () => getOperaCandidates(restaurantId, reservationId),
    enabled,
    // Every call costs Oracle transactions -- a failed or slow candidates fetch must not be
    // retried, and refocusing the drawer's window/tab must not re-trigger it.
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * The panel's own view of a reservation's hotel link -- always enabled, so the stored link
 * renders immediately. Once a reservation is linked, OperaLinkController.getCandidates answers
 * straight from Peak's own database, without a live OHIP search, so this no longer costs the
 * drawer an 11-second wait the way the old full-window scan did.
 */
export const useOperaLink = (reservationId: string) =>
  useOperaCandidatesEndpoint(reservationId);

/**
 * The candidate list itself: the stored suggestion (if any) plus candidates matched by
 * email/phone/name. Only worth keeping active while there is still something to search for --
 * an unlinked reservation (the default guest-contact search) or the "find a different stay"
 * modal open and asking for a fresh list -- since a linked reservation gets `candidates: []`
 * from this same endpoint regardless (see useOperaLink's comment). Same query key/fn as
 * useOperaLink, so react-query serves one cached response rather than firing the request twice.
 */
export const useOperaLinkCandidates = (
  reservationId: string,
  enabled: boolean,
) => useOperaCandidatesEndpoint(reservationId, enabled);

const invalidateOperaAndOccupantQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  restaurantId: string,
  reservationId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: operaLinkQueryKey(restaurantId, reservationId),
  });
  // Same key `useFetchOccupantDetails` uses for the drawer's ServiceReservation,
  // so the badge/summary reflect the new link status immediately.
  queryClient.invalidateQueries({
    queryKey: [
      'restaurants',
      restaurantId,
      'occupants',
      'reservation',
      reservationId,
    ],
  });
};

export const useLinkOperaReservation = (
  reservationId: string,
  onSuccess?: () => void,
) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operaReservationId: string) =>
      linkOperaReservation(restaurantId, reservationId, operaReservationId),
    onError: (error) => {
      errorToast({
        message:
          error instanceof ApiError
            ? `Failed to link hotel reservation: ${error.message}`
            : 'Failed to link hotel reservation. Please try again.',
      });
    },
    onSuccess: (link) => {
      successToast({ message: 'Hotel reservation linked.' });
      onSuccess?.();
      // LinkReservationToOperaService now runs the same read RefreshOperaLinkService performs
      // before this mutation resolves, so `link` here already carries status, room, VIP,
      // companions, allergies, occasion, hotel notes and a last-synced timestamp -- write it
      // straight into the cache so the panel shows all of that the instant the mutation
      // resolves, rather than waiting on the invalidation below to refetch it.
      queryClient.setQueryData(
        operaLinkQueryKey(restaurantId, reservationId),
        (previous?: { candidates: unknown[]; hotelUnavailable: boolean }) => ({
          link,
          candidates: previous?.candidates ?? [],
          hotelUnavailable: false,
        }),
      );
      invalidateOperaAndOccupantQueries(
        queryClient,
        restaurantId,
        reservationId,
      );
    },
  });
};

export const useRejectOperaMatch = (
  reservationId: string,
  onSuccess?: () => void,
) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operaReservationId: string) =>
      rejectOperaMatch(restaurantId, reservationId, operaReservationId),
    onError: (error) => {
      errorToast({
        message:
          error instanceof ApiError
            ? `Failed to reject suggested match: ${error.message}`
            : 'Failed to reject suggested match. Please try again.',
      });
    },
    onSuccess: () => {
      successToast({ message: 'Suggested match rejected.' });
      onSuccess?.();
      invalidateOperaAndOccupantQueries(
        queryClient,
        restaurantId,
        reservationId,
      );
    },
  });
};

// The Hotel Sync page's "Hotel guests" section query key, exported so a row's Refresh
// mutation can invalidate it without duplicating the key shape.
export const hotelGuestsQueryKey = (
  restaurantId: string,
  from: string,
  to: string,
) => ['restaurants', restaurantId, 'opera', 'links', from, to] as const;

// Manager-only endpoint: `enabled` keeps a host from firing a request that would 403.
export const useOperaHotelGuests = (
  from: string,
  to: string,
  enabled: boolean,
) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: hotelGuestsQueryKey(restaurantId, from, to),
    queryFn: () => getOperaHotelGuests(restaurantId, from, to),
    enabled,
  });
};

// Manager-only endpoint: `enabled` keeps a host from firing a request that would 403.
export const useOperaMatchAttempts = (enabled: boolean) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['restaurants', restaurantId, 'opera', 'match-attempts'],
    queryFn: () => getOperaMatchAttempts(restaurantId),
    enabled,
  });
};

// Manager-only endpoint: `enabled` keeps a host's Stats drawer from firing a request that
// would 403 and surface an error toast for a number they are not shown anyway.
export const useOperaCovers = (date: string, enabled: boolean) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['restaurants', restaurantId, 'opera', 'covers', date],
    queryFn: () => getOperaCovers(restaurantId, date),
    enabled,
  });
};

// Every other Opera web surface fetches through react-query from this file, so this one does
// too, even though its sibling blocks in the Guest Book (GuestTags, GuestNotes) still use
// useAbortEffect. One Opera data-fetching pattern beats matching the folder it renders in.
export const useOperaGuestProfile = (guestId: string) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['restaurants', restaurantId, 'opera', 'guestProfile', guestId],
    queryFn: () => getOperaGuestProfile(restaurantId, guestId),
  });
};

export const useRefreshOperaLink = (
  reservationId: string,
  onSuccess?: () => void,
) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => refreshOperaLink(restaurantId, reservationId),
    onError: (error) => {
      errorToast({
        message:
          error instanceof ApiError
            ? `Failed to refresh hotel details: ${error.message}`
            : 'Failed to refresh hotel details. Please try again.',
      });
    },
    // A refresh that could not reach Opera still resolves 200, with the reason on the link
    // (RefreshOperaLinkService persists it instead of throwing) -- reporting success here
    // would tell the host the data is current when it is not.
    onSuccess: (link) => {
      if (link.lastSyncError) {
        errorToast({
          message: `Could not reach the hotel system: ${link.lastSyncError}`,
        });
      } else {
        successToast({ message: 'Hotel details refreshed.' });
      }
      onSuccess?.();
      invalidateOperaAndOccupantQueries(
        queryClient,
        restaurantId,
        reservationId,
      );
    },
  });
};
